import React from 'react';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import './ens-loaders.css';

const PLUS = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" aria-hidden="true">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const Orbit: React.FC<{ side: 'back' | 'front'; second?: boolean }> = ({ side, second }) => (
  <span className={`ens-orbit${second ? ' o2' : ''} ${side}`}>
    <span className="ens-path" />
    <span className="ens-spin"><i /></span>
  </span>
);

/** The animated ++ mark. Decorative: always aria-hidden. `icon` (px) fits an sm mark into an icon slot, e.g. a button's startIcon. */
export const EnsMark: React.FC<{ size: 'lg' | 'md' | 'sm'; icon?: number }> = ({ size, icon }) => {
  const core = (
    <span className="ens-core">
      <span className="ens-trace" />
      <span className="ens-logo">{PLUS}{PLUS}</span>
    </span>
  );
  if (size === 'sm') {
    return icon ? (
      <span className="ens-mark sm ens-icon" style={{ '--ens-icon': `${icon}px` } as React.CSSProperties} aria-hidden="true">{core}</span>
    ) : (
      <span className="ens-mark sm" aria-hidden="true">{core}</span>
    );
  }
  const lg = size === 'lg';
  return (
    <span className={`ens-mark ${size}`} aria-hidden="true">
      <span className="ens-halo" />
      <span className="ens-ripple" />
      {lg && <span className="ens-ripple" />}
      <Orbit side="back" />
      {lg && <Orbit side="back" second />}
      {core}
      <Orbit side="front" />
      {lg && <Orbit side="front" second />}
    </span>
  );
};

const Label: React.FC<{ text: string }> = ({ text }) => (
  <div className="ens-label">
    <p className="ens-text">{text}</p>
    <span className="ens-bar" aria-hidden="true" />
  </div>
);

export interface EnsLoaderProps {
  label?: string;
  /** `overlay` covers a position:relative parent; default sits in normal flow. */
  overlay?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

/** Section / card loader. */
export const EnsLoader: React.FC<EnsLoaderProps> = ({ label = 'Loading…', overlay, className, style }) => (
  <div
    className={['ens-section', overlay ? '' : 'ens-block', className].filter(Boolean).join(' ')}
    style={style}
    role="status"
    aria-live="polite"
    aria-busy="true"
  >
    <EnsMark size="md" />
    <Label text={label} />
  </div>
);

/** Full-page loader. Mount while the app is not ready; `hiding` fades it out (no longer a status). */
export const EnsPageLoader: React.FC<{ label?: string; hiding?: boolean }> = ({ label = 'Loading EnsoMirai++…', hiding }) => (
  <div
    className={hiding ? 'ens-page is-hiding' : 'ens-page'}
    {...(hiding ? { 'aria-hidden': true } : { role: 'status', 'aria-live': 'polite' as const, 'aria-busy': true })}
  >
    <EnsMark size="lg" />
    <Label text={label} />
  </div>
);

/** Small inline status: ++ mark + text. */
export const EnsInline: React.FC<{ label?: React.ReactNode; icon?: number; className?: string; style?: React.CSSProperties }> = ({
  label,
  icon,
  className,
  style,
}) => (
  <span className={className ? `ens-inline ${className}` : 'ens-inline'} style={style} role="status" aria-live="polite">
    <EnsMark size="sm" icon={icon} />
    {label}
  </span>
);

/** Skeleton bar. `shape`: default line, `round` pill, `block` box. */
export const EnsSkeleton: React.FC<{
  width?: number | string;
  height?: number | string;
  shape?: 'round' | 'block';
  style?: React.CSSProperties;
  sx?: SxProps<Theme>;
}> = ({ width, height, shape, style, sx }) => (
  <Box component="span" className={shape ? `ens-skel ${shape}` : 'ens-skel'} style={{ width, height, ...style }} sx={sx} aria-hidden="true" />
);

/** Indeterminate bar: drop-in for MUI's indeterminate LinearProgress (full width, 4px). */
export const EnsBar: React.FC<{ sx?: SxProps<Theme>; 'aria-label'?: string }> = ({ sx, 'aria-label': ariaLabel }) => (
  <Box component="span" className="ens-bar ens-bar-full" role="progressbar" aria-label={ariaLabel} sx={sx} />
);

export default EnsLoader;
